import { equals, matches, noop, some } from 'lodash/fp';

import type { GroupedVariables, RequestMiddleware } from './gqlEngineUtils';
import { ObjectSelection, getObjectSelection } from './gqlObjectSelection';

const cache = new Map<
	string,
	Array<{
		groupedVariables: GroupedVariables;
		objectSelection: ObjectSelection;
		pendingRequest: Promise<any>;
	}>
>();

type Options = {
	/**
	 * The default TTL (milliseconds) of the request in the cache.
	 */
	defaultTTL?: number;
	operationIdTTL?: Record<string, number>;
};

export const memoPendingRequestsMiddleware: (cachePolicy?: Options) => RequestMiddleware =
	({ defaultTTL = 0, operationIdTTL } = {}) =>
	(next) =>
	(request) => {
		if (
			// memoize only queries
			request.method !== `get` ||
			// if you need to skip network cache, set `context: { skipNetworkCache: true }` on the (use)Query ...
			request.context.skipNetworkCache ||
			// ... or use the `@skipNetworkCache` GQL directive on operationId field
			some({ name: { value: `skipNetworkCache` } }, request.info.field.directives)
		) {
			return next(request);
		}
		const { groupedVariables, operationId } = request;
		const objectSelection = getObjectSelection(request.info, request.context.variables);
		let cacheForOperationId = cache.get(operationId);
		if (!cacheForOperationId) {
			cacheForOperationId = [];
			cache.set(operationId, cacheForOperationId);
		} else {
			const cachedPendingRequest = cacheForOperationId.find(
				(cachedEntry) =>
					equals(groupedVariables, cachedEntry.groupedVariables) &&
					matches(objectSelection, cachedEntry.objectSelection)
			);
			if (cachedPendingRequest) {
				return cachedPendingRequest.pendingRequest;
			}
		}
		const pendingRequest = next(request);
		const cacheEntry = { groupedVariables, objectSelection, pendingRequest };
		const cleanup = () => {
			cacheForOperationId!.splice(cacheForOperationId!.indexOf(cacheEntry), 1);
		};
		cacheForOperationId.push(cacheEntry);
		const timeToLive = operationIdTTL?.[operationId] ?? defaultTTL;
		if (!timeToLive || process.env.NODE_ENV === `test`) {
			// do not postpone cache cleanup during test as it breaks quick sequential testing
			pendingRequest.catch(noop).finally(cleanup);
		} else {
			// postpone the cache cleanup _on success only_, the cache is cleared immediately on error allow a retry attempt.
			// The cleanup in error position also suppress errors, which is intended here, we dont want to produce unhandled
			// rejection inside this middleware - the error will be propagated to Apollo, but since every chain of promise
			// creates new instance, our `.then` here produces new promise where we are not interested in promise rejections.
			pendingRequest.then(() => {
				window.setTimeout(cleanup, timeToLive);
			}, cleanup);
		}

		return pendingRequest;
	};
